import type { AnyAction, Dispatch } from 'redux';
import type { Channel } from 'redux-saga';
import { actionChannel, call, fork, select, take } from 'redux-saga/effects';

import { signInAction, signOutAction } from '@/domain/env/envActions';
import { isAuthorizedSelector } from '@/domain/env/envSelector';
import { companySignInAction } from '@/domain/companies/companiesActions';
import { ensureWorkerGetMessagesCreator } from '@/domain/messages/sagas';
import { MultyWorker } from '@/lib/worker';
import { resetMessages } from '@/domain/organization/organizationActions';
import { LOCATION_CHANGE } from '@/domain/router/redux/reduxActions';
import { setRouteCompanyIdAction } from '@/domain/router/RouterActions';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';

// Both write the company in scope.
const COMPANY_CHANGES = [setRouteCompanyIdAction.type, LOCATION_CHANGE];
const RESTARTS = [
  companySignInAction.success,
  resetMessages.type,
  signInAction.success,
  signOutAction.success,
];

// The poll takes its CompanyId once, when it starts (the worker cannot read the store), so it is restarted on
// every change of the current company: it names the company in the URL and none on a route that names none.
function* restartPoller(worker: MultyWorker): Generator {
  worker.terminate('getMessages');
  const isAuthorized = (yield select(isAuthorizedSelector)) as boolean;
  if (isAuthorized) yield call(worker.init, ensureWorkerGetMessagesCreator());
}

function* nextRestart(channel: Channel<AnyAction>, company: string | null): Generator {
  while (true) {
    const { type } = (yield take(channel)) as AnyAction;
    if (!COMPANY_CHANGES.includes(type) || (yield select(currentCompanyIdSelector)) !== company) return;
  }
}

export default function* watchMessage(dispatch: Dispatch): Generator {
  const worker = new MultyWorker(dispatch);
  // Buffers what arrives while a restart waits for the token, so no company change is missed.
  const channel = (yield actionChannel([...RESTARTS, ...COMPANY_CHANGES])) as Channel<AnyAction>;
  yield fork(worker.listen);

  // One restart at a time, so a slower restart can never post a company the user has already left.
  while (true) {
    const company = (yield select(currentCompanyIdSelector)) as string | null;
    yield call(restartPoller, worker);
    yield call(nextRestart, channel, company);
  }
}
